// SEO do portal white-label (ACH-005). Separado do portalStore para manter a lógica de
// fallback pura e testável (o portalStore tem side-effects de módulo que dependem de window).

export type PortalSeoInput = {
  portalName: string
  portalDescription: string | null
  seoTitle: string | null
  seoDescription: string | null
}

export type PortalSeo = {
  title: string | null
  description: string | null
  ogTitle: string | null
  ogDescription: string | null
}

/**
 * Resolve os metadados de SEO do portal com fallback simétrico (ACH-005):
 * - `title`       = seo_title  ou (fallback) portal_name
 * - `description` = seo_description ou (fallback) portal_description
 * Com portal_name/portal_description presentes (o caso normal), o portal do cliente não
 * fica sem `<title>`/`<meta description>`. Se todas as fontes vierem vazias, o campo resolve
 * para `null` — `applyPortalSeo` então não toca a tag correspondente (nada a aplicar).
 * `og:*` espelham os valores resolvidos. String vazia conta como ausente (cai no fallback).
 */
export function computePortalSeo(theme: PortalSeoInput): PortalSeo {
  const title = theme.seoTitle || theme.portalName || null
  const description = theme.seoDescription || theme.portalDescription || null
  return { title, description, ogTitle: title, ogDescription: description }
}

// Os call sites passam apenas nomes/propriedades constantes ('description', 'og:title',
// 'og:description') — nunca entrada do reseller — então a interpolação no seletor é segura.
// O `content` (portal_name/description) vai só para o atributo via `.content`, sem parse de HTML.
function upsertMetaByName(name: string, content: string): void {
  let meta = document.querySelector<HTMLMetaElement>(`meta[name='${name}']`)
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = name
    document.head.appendChild(meta)
  }
  meta.content = content
}

function upsertMetaByProperty(property: string, content: string): void {
  let meta = document.querySelector<HTMLMetaElement>(`meta[property='${property}']`)
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute('property', property)
    document.head.appendChild(meta)
  }
  meta.content = content
}

/** Aplica os metadados de SEO resolvidos no `<head>` do documento. */
export function applyPortalSeo(theme: PortalSeoInput): void {
  const seo = computePortalSeo(theme)
  if (seo.title) document.title = seo.title
  if (seo.description) upsertMetaByName('description', seo.description)
  if (seo.ogTitle) upsertMetaByProperty('og:title', seo.ogTitle)
  if (seo.ogDescription) upsertMetaByProperty('og:description', seo.ogDescription)
}
